import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  formatPeso,
  isRepeating,
  openOccurrences,
  receivedOccurrences,
  receiptOn,
  repeatOf,
  type ExpectedMoneyLike,
  type ExpectedRepeat,
  type LocalDate,
} from '@tahi/core';
import {
  addExpected,
  deleteExpected,
  editExpected,
  receiveExpected,
  restoreExpected,
  skipExpected,
  undoReceiveExpected,
} from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Sheet, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { useGoBack } from '../../spending/hooks';
import { AccountChips } from '../components/AccountChips';
import { ChoiceChips } from '../components/ChoiceChips';
import { copy, errorText } from '../copy';
import { dateWithYear, dayShort, minorToInput, parsePesoInput } from '../format';
import { useAccounts, useBudgetSettings, useExpectedMoney, useToday } from '../hooks';
import { FOCUS, LABEL, QUIET_LINK } from '../styles';

const BACK = '/m/budget/accounts';

const REPEATS: { value: ExpectedRepeat; label: string }[] = [
  { value: 'none', label: copy.repeatNone },
  { value: 'monthly', label: copy.repeatMonthly },
  { value: 'yearly', label: copy.repeatYearly },
];

/** Where "Received" on one item (or, for a repeating one, one occurrence) goes. */
export function receivePath(id: string, occurrenceDate?: LocalDate | null): string {
  return `/m/budget/expected/${id}/received${occurrenceDate ? `?on=${occurrenceDate}` : ''}`;
}

/** v0.5 "Coming in": add or edit expected money (not income). */
export function ExpectedFormRoute() {
  const { id } = useParams();
  const items = useExpectedMoney();
  const goBack = useGoBack(BACK);
  if (items === undefined) return null;
  const item = id ? items.find((e) => e.id === id) : undefined;
  if (id && !item) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.editExpected}>
        <p className="text-muted">{errorText('not_found')}</p>
      </Sheet>
    );
  }
  return <ExpectedForm item={item} />;
}

/** v0.7.4: a repeating item's next occurrences (Received, Didn't come) and its received ones (Undo). */
function Occurrences({ item }: { item: ExpectedMoneyLike }) {
  const env = useCommandEnv();
  const today = useToday();
  const [error, setError] = useState<string | null>(null);
  const open = openOccurrences(item, today, 3);
  const received = receivedOccurrences(item);

  async function skip(date: LocalDate) {
    const r = await skipExpected(env, item.id, date);
    if (!r.ok) return setError(errorText(r.error));
    showToast({
      message: copy.skippedOnToast(dayShort(date)),
      action: { label: copy.undo, onPress: () => void undoReceiveExpected(env, item.id, date) },
    });
  }

  async function undo(date: LocalDate) {
    const r = await undoReceiveExpected(env, item.id, date);
    if (!r.ok) setError(errorText(r.error));
  }

  return (
    <div className="flex flex-col gap-3">
      {open.length > 0 ? (
        <section aria-label={copy.nextOnes} className="flex flex-col gap-1">
          <h3 className={LABEL}>{copy.nextOnes}</h3>
          <ul className="divide-y divide-divider">
            {open.map((o) => (
              <li key={o.key} className="flex min-h-11 items-center justify-between gap-2 py-1">
                <span className="min-w-0 text-fg">
                  {dateWithYear(o.date)}
                  {o.late ? (
                    <span className="block text-sm text-muted">{copy.notYetReceived}</span>
                  ) : null}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  <Link
                    to={receivePath(item.id, o.occurrenceDate)}
                    aria-label={`${copy.markReceived}: ${dateWithYear(o.date)}`}
                    className={QUIET_LINK}
                  >
                    {copy.markReceived}
                  </Link>
                  <button
                    type="button"
                    aria-label={`${copy.didntCome}: ${dateWithYear(o.date)}`}
                    onClick={() => void skip(o.occurrenceDate)}
                    className={`min-h-11 px-2 text-sm text-muted ${FOCUS}`}
                  >
                    {copy.didntCome}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {received.length > 0 ? (
        <section aria-label={copy.receivedOnes} className="flex flex-col gap-1">
          <h3 className={LABEL}>{copy.receivedOnes}</h3>
          <ul className="divide-y divide-divider">
            {received.map((r) => (
              <li key={r.id} className="flex min-h-11 items-center justify-between gap-2 py-1">
                <span className="min-w-0 text-fg">
                  {copy.receivedOccurrence(
                    dateWithYear(r.occurrenceDate),
                    r.amountMinor ?? item.amountMinor,
                  )}
                  {r.receivedDate && r.receivedDate !== r.occurrenceDate ? (
                    <span className="block text-sm text-muted">
                      {copy.receivedOn}: {dayShort(r.receivedDate)}
                    </span>
                  ) : null}
                </span>
                <Button
                  variant="secondary"
                  aria-label={`${copy.undoReceived}: ${dateWithYear(r.occurrenceDate)}`}
                  onClick={() => void undo(r.occurrenceDate)}
                >
                  {copy.undoReceived}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ExpectedForm({ item }: { item?: ExpectedMoneyLike }) {
  const env = useCommandEnv();
  const navigate = useNavigate();
  const today = useToday();
  const accounts = useAccounts() ?? [];
  const settings = useBudgetSettings();
  const goBack = useGoBack(BACK);
  const [name, setName] = useState(item?.name ?? '');
  const [amount, setAmount] = useState(item ? minorToInput(item.amountMinor) : '');
  const [date, setDate] = useState(item?.expectedDate ?? today);
  const [repeat, setRepeat] = useState<ExpectedRepeat>(item ? repeatOf(item) : 'none');
  const [until, setUntil] = useState<LocalDate | null>(item?.repeatUntil ?? null);
  const [accountId, setAccountId] = useState<string | null>(
    item ? item.accountId : (settings?.incomeAccountId ?? null),
  );
  const [error, setError] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const minor = parsePesoInput(amount);
  const repeating = repeat !== 'none';

  async function save() {
    if (name.trim() === '') return setError(errorText('name_required'));
    if (minor === null) return setError(errorText('bad_amount'));
    const input = {
      name,
      amountMinor: minor,
      expectedDate: date,
      accountId,
      repeat,
      repeatUntil: repeating ? until : null,
    };
    const r = item ? await editExpected(env, item.id, input) : await addExpected(env, input);
    if (!r.ok) return setError(errorText(r.error));
    goBack();
  }

  async function remove() {
    if (!item) return;
    if (!armed) return setArmed(true);
    const r = await deleteExpected(env, item.id);
    if (r.ok) {
      const removed = r.removed;
      showToast({
        message: copy.deletedExpected(item.name),
        action: { label: copy.undo, onPress: () => void restoreExpected(env, item.id, removed) },
      });
    }
    navigate(BACK, { replace: true });
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && goBack()}
      title={item ? copy.editExpected : copy.addExpected}
      description={copy.comingInNote}
      footer={
        <Button variant="primary" block onClick={() => void save()}>
          {copy.save}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {item && !isRepeating(item) && item.status === 'received' ? (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted">
              {copy.receivedTitleOf(item.name)} · {dayShort(item.receivedDate ?? item.expectedDate)}
            </p>
            <Button
              variant="secondary"
              onClick={() => void undoReceiveExpected(env, item.id).then(() => goBack())}
            >
              {copy.undoReceived}
            </Button>
          </div>
        ) : null}
        {item && isRepeating(item) ? <Occurrences item={item} /> : null}
        <TextField
          label={copy.expectedName}
          placeholder={copy.expectedNamePlaceholder}
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label={copy.expectedAmount}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <ChoiceChips<ExpectedRepeat>
          label={copy.repeats}
          value={repeat}
          options={REPEATS}
          onChange={setRepeat}
        />
        <TextField
          label={repeating ? copy.firstExpected : copy.expectedDate}
          type="date"
          value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
        {repeating ? (
          <div className="flex flex-col gap-1">
            <TextField
              label={copy.repeatUntil}
              hint={copy.repeatUntilHint}
              type="date"
              value={until ?? ''}
              min={date}
              onChange={(e) => setUntil(e.target.value || null)}
            />
            {until ? (
              <Button variant="secondary" className="self-start" onClick={() => setUntil(null)}>
                {copy.noEnd}
              </Button>
            ) : null}
          </div>
        ) : null}
        {accounts.length > 0 ? (
          <AccountChips
            label={copy.landsIn}
            accounts={accounts}
            value={accountId}
            onChange={setAccountId}
          />
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {item ? (
          <Button variant="destructive" block onClick={() => void remove()}>
            {armed ? copy.confirmDelete : copy.delete}
          </Button>
        ) : null}
      </div>
    </Sheet>
  );
}

/**
 * "Received": the amount can differ and the account can change; deposited, never income.
 * v0.7.4: a repeating item's occurrence is named by ?on=<its scheduled day>.
 */
export function ReceiveExpectedRoute() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const items = useExpectedMoney();
  const goBack = useGoBack(BACK);
  if (items === undefined) return null;
  const item = items.find((e) => e.id === id);
  const on = params.get('on');
  const repeating = item ? isRepeating(item) : false;
  const problem = !item
    ? 'not_found'
    : repeating
      ? !on
        ? 'not_an_occurrence'
        : receiptOn(item, on)?.status === 'received'
          ? 'already_received'
          : null
      : item.status === 'received'
        ? 'already_received'
        : null;
  if (!item || problem) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.markReceived}>
        <p className="text-muted">{errorText(problem ?? 'not_found')}</p>
      </Sheet>
    );
  }
  return <ReceiveSheet item={item} occurrenceDate={repeating ? on : null} />;
}

function ReceiveSheet({
  item,
  occurrenceDate,
}: {
  item: ExpectedMoneyLike;
  occurrenceDate: LocalDate | null;
}) {
  const env = useCommandEnv();
  const today = useToday();
  const accounts = useAccounts() ?? [];
  const settings = useBudgetSettings();
  const goBack = useGoBack(BACK);
  const [amount, setAmount] = useState(minorToInput(item.amountMinor));
  const [date, setDate] = useState(
    occurrenceDate && occurrenceDate < today ? occurrenceDate : today,
  );
  const [accountId, setAccountId] = useState<string | null>(
    item.accountId ?? settings?.incomeAccountId ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const minor = parsePesoInput(amount);
  const at = occurrenceDate ?? undefined;

  async function save() {
    if (minor === null) return setError(errorText('bad_amount'));
    const r = await receiveExpected(env, item.id, { amountMinor: minor, date, accountId }, at);
    if (!r.ok) return setError(errorText(r.error));
    showToast({
      message: copy.receivedExpectedToast(
        minor,
        accounts.find((a) => a.id === accountId)?.name ?? null,
      ),
      action: { label: copy.undo, onPress: () => void undoReceiveExpected(env, item.id, at) },
    });
    goBack();
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && goBack()}
      title={copy.receivedTitleOf(item.name)}
      description={
        occurrenceDate
          ? `${dateWithYear(occurrenceDate)} · ${formatPeso(item.amountMinor, { cents: 'auto' })}`
          : undefined
      }
      footer={
        <Button variant="primary" block disabled={minor === null} onClick={() => void save()}>
          {copy.markReceived}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label={copy.amount}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <TextField
          label={copy.receivedOn}
          type="date"
          value={date}
          max={today}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
        {accounts.length > 0 ? (
          <AccountChips
            label={copy.landsIn}
            accounts={accounts}
            value={accountId}
            onChange={setAccountId}
          />
        ) : null}
        <p className="text-sm text-muted">{copy.comingInNote}</p>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
